// Airports layer family.
//
// Extracted from addOverlays.ts as HI-1 batch 5 round 2 (audit 2026-05-22).
// Pure mechanical move — every source, layer, paint expression,
// layout property, and minzoom/maxzoom is byte-identical to its
// pre-extraction form. Source-id idempotency guard preserved.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { ensureAirportIcons, AIRPORT_ICON_EXPRESSION } from '../airportIcons';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';

/**
 * Mount the airports source + its layers. Idempotent on the source.
 */
export function addAirportsLayers(map: MlMap): void {
    if (!map.getSource('airports')) {
      map.addSource('airports', { type: 'geojson', data: EMPTY_FC });
      // Kick off the icon load — fire and forget; MapLibre re-renders
      // automatically once each named image is registered.
      void ensureAirportIcons(map);
      // 2026-05-28: per-type staggered minzoom via layer filter.
      // Boss request — reduce small-airport clutter at low zoom.
      // The layer-level minzoom: 5 still applies as a hard floor,
      // but inside that range each bucket gates itself:
      //   large_airport + scheduled (towered) → visible from z=5
      //   medium_airport                       → visible from z=6
      //   small_airport + seaplane_base        → visible from z=8
      // Server (api/bbox.ts) staggers the wire payload along the
      // same boundaries; this filter is the authoritative visual
      // control even if a buggy / stale client receives extra rows.
      const airportsTypeFilter: any = [
        'any',
        ['==', ['get', 'type'], 'large_airport'],
        ['==', ['get', 'scheduled'], true],
        ['all',
          ['==', ['get', 'type'], 'medium_airport'],
          ['>=', ['zoom'], 6],
        ],
        ['all',
          ['any',
            ['==', ['get', 'type'], 'small_airport'],
            ['==', ['get', 'type'], 'seaplane_base'],
          ],
          ['>=', ['zoom'], 8],
        ],
      ];
      map.addLayer({
        id: 'airports-icon',
        type: 'symbol',
        source: 'airports',
        minzoom: 5,
        filter: airportsTypeFilter,
        layout: {
          'icon-image': AIRPORT_ICON_EXPRESSION,
          'icon-size': [
            // Smaller at low zoom so dense areas don't overwhelm; full
            // size at typical en-route zoom (8–10) where the user is
            // actually picking off airports. Bumped up vs v1 — the boss
            // wanted them "slightly larger" so they read at a glance.
            'interpolate', ['linear'], ['zoom'],
            5,  0.50,
            7,  0.75,
            9,  1.00,
            12, 1.25,
          ],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
      map.addLayer({
        id: 'airports-label',
        type: 'symbol',
        source: 'airports',
        minzoom: 9,
        filter: airportsTypeFilter,
        layout: {
          'text-field': ['get', 'ident'],
          'text-size': 10,
          'text-offset': [0, 1.2],
          'text-anchor': 'top',
          'text-font': ['Noto Sans Regular'],
        },
        paint: {
          'text-color': '#e8edf3',
          'text-halo-color': '#0b0d0f',
          'text-halo-width': 1.2,
        },
      });
    }
}

registerLayerFamily({
  id: 'airports',
  label: 'Airports',
  sourceId: 'airports',
  layerIds: ['airports-icon', 'airports-label'],
  mount: (map) => addAirportsLayers(map),
  visibilitySelector: (l) => l.airports,
  applyState: (map) => {
    if (!map.getLayer('airports-icon')) return;
    const show = useLayerVisibilityStore.getState().layers.airports;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('airports-icon', 'visibility', v);
    map.setLayoutProperty('airports-label', 'visibility', v);
  },
});
